<script lang="ts" setup>
import { useTzDate } from '@/composable/useTzDate'
import Card from 'primevue/card'
import { Checkbox, Divider } from 'primevue'
import { computed, onMounted, ref } from 'vue'
import { useEventsStore } from '@/stores/eventsStore'
import EventsCards from '@/components/EventsCards.vue'
import { capitalize } from 'lodash'

export interface DateEvents {
  time: string
  isListed: boolean
  asset: string
  exchange: string
  severity?: string
  diff: number
  swap: boolean
  startAt: number
  eventType: 'listing' | 'delisting'
}

const eventsStore = useEventsStore()
const { yyyymmdd, hhmm } = useTzDate()
const showFutures = ref(true)
const showSpot = ref(true)
const showListings = ref(true)
const showDelistings = ref(true)
const exsFilter = ref<string[]>([])

const datesEvents = computed(() => {
  const byDates: Record<string, DateEvents[]> = {}
  const now = new Date()
  eventsStore.events.forEach((x) => {
    if (!x.start_at) return
    const startDate = new Date(x.start_at)
    if (isNaN(startDate.getTime())) return
    const diff = (now.getTime() - startDate.getTime()) / 1000
    if (diff > 86400 * 7 || diff < -86400 * 7) {
      return
    }
    const dateStr = yyyymmdd(startDate)
    const timeStr = hhmm(startDate)
    if (!(dateStr in byDates)) {
      byDates[dateStr] = []
    }
    const symbolName = x.symbol?.name
    if (!symbolName) return
    const asset = symbolName.split('/')[0]
    if (!asset) {
      return
    }
    const exchangeName = x.exchange?.name
    if (!exchangeName) return
    if (exsFilter.value.length > 0 && !exsFilter.value.includes(exchangeName)) {
      return
    }
    const isSwap = symbolName.includes(':')
    if ((!showFutures.value && isSwap) || (!showSpot.value && !isSwap)) {
      return
    }
    if (x.event_type === 'listing' && !showListings.value) {
      return
    }
    if (x.event_type === 'delisting' && !showDelistings.value) {
      return
    }
    byDates[dateStr]?.push({
      time: timeStr,
      isListed: x.is_listed,
      asset: asset,
      severity: x.event_type === 'delisting' ? 'danger' : 'success',
      exchange: exchangeName,
      diff: diff,
      swap: isSwap,
      startAt: startDate.getTime(),
      eventType: x.event_type,
    })
  })
  const ret = Object.fromEntries(
    Object.entries(byDates).sort((a, b) => {
      const dateA = new Date(a[0].split('.').reverse().join('-'))
      const dateB = new Date(b[0].split('.').reverse().join('-'))
      return dateA.getTime() - dateB.getTime()
    }).reverse()
  )

  return ret
})

const exchanges = computed(() => {
  const set = new Set<string>()
  eventsStore.events.forEach((x) => {
    const name = x.exchange?.name
    if (name) set.add(name)
  })
  return Array.from(set).sort()
})

const toggleExchange = (ex: string) => {
  if (exsFilter.value.includes(ex)) {
    exsFilter.value = exsFilter.value.filter((e) => e !== ex)
  } else {
    exsFilter.value.push(ex)
  }
}

onMounted(async () => {
  await eventsStore.fetch()
})
</script>

<template>
  <div>
    <Card class="md:mb-6 rounded-none md:rounded-2xl shadow-sm" pt:body:class="px-4 py-3">
      <template #content>
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center gap-2">
            <Checkbox v-model="showFutures" inputId="showFutures" name="showFutures" binary size="small" />
            <label class="cursor-pointer text-sm font-medium" for="showFutures">
              {{ $t('listings.showFutures') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="showSpot" inputId="showSpot" name="showSpot" binary size="small" />
            <label class="cursor-pointer text-sm font-medium" for="showSpot">
              {{ $t('listings.showSpot') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="showListings" inputId="showListings" name="showListings" binary size="small" />
            <label class="cursor-pointer text-sm font-medium" for="showListings">
              {{ $t('listings.showListings') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="showDelistings" inputId="showDelistings" name="showDelistings" binary size="small" />
            <label class="cursor-pointer text-sm font-medium" for="showDelistings">
              {{ $t('listings.showDelistings') }}
            </label>
          </div>
          <Divider layout="vertical" class="hidden sm:block !h-8" />
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm text-gray-500 mr-1">{{ $t('filters.exchanges') }}:</span>
            <button v-for="ex in exchanges" :key="ex" @click="toggleExchange(ex)"
              class="exchange-chip px-3 py-1 rounded-full text-xs font-semibold transition-all border"
              :class="exsFilter.includes(ex)
                ? 'bg-primary text-white border-primary'
                : 'bg-transparent text-gray-500 border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:text-gray-400'">
              {{ capitalize(ex) }}
            </button>
            <button v-if="exsFilter.length > 0" @click="exsFilter = []"
              class="text-xs text-gray-400 hover:text-primary underline ml-1">
              {{ $t('listings.reset') }}
            </button>
          </div>
        </div>
      </template>
    </Card>
    <EventsCards class="rounded-none md:rounded-2xl" :value="datesEvents" :loading="eventsStore.loading"></EventsCards>
  </div>
</template>

<style scoped>
.calendar-cell {
  border: 0.5px solid rgba(0, 0, 0, 0.045);
}

.calendar-header {
  text-align: center;
}
</style>
